import React, { useMemo, useCallback, useState, useEffect } from 'react';
import './DataTable.css';

const DataTable = ({ 
  data = [], 
  columns, 
  onSelectionChange,
  selectable = true,
  loading = false,
  error = null,
  emptyMessage = 'No hay datos registrados',
  emptyStateNoReload = false,
  onRetry = null,
  pageSize = 20,
  currentPage = 1,
  onPageChange,
  className = '',
  idField = 'id',
  ...props 
}) => {
  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    
    const term = searchTerm.toLowerCase().trim();
    return data.filter(item => {
      return Object.values(item).some(value => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(term);
      });
    });
  }, [data, searchTerm]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr);
      }
      return bStr.localeCompare(aStr);
    });
    
    return sorted;
  }, [filteredData, sortConfig]);

  useEffect(() => {
    if (onPageChange) {
      const totalPages = Math.ceil(sortedData.length / pageSize);
      if (currentPage > totalPages && totalPages > 0) {
        onPageChange(1);
      }
    }
  }, [sortedData.length, currentPage, onPageChange, pageSize]);

  const handleSort = useCallback((column) => {
    if (column.disableSortBy) return;
    
    setSortConfig(current => ({
      key: column.accessor,
      direction: current.key === column.accessor && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = pageSize 
    ? sortedData.slice(startIndex, endIndex)
    : sortedData;

  const handleSelectionChange = useCallback((id) => {
    const newSelected = selected.includes(id)
      ? selected.filter(selectedId => selectedId !== id)
      : [...selected, id];
    setSelected(newSelected);
    setSelectAll(newSelected.length === paginatedData.length);
    onSelectionChange?.(newSelected);
  }, [selected, onSelectionChange, paginatedData.length]);

  const handleSelectAll = useCallback(() => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const allIds = paginatedData.map(item => item[idField]);
    const newSelected = newSelectAll ? allIds : [];
    setSelected(newSelected);
    onSelectionChange?.(newSelected);
  }, [selectAll, paginatedData, onSelectionChange, idField]);

  const columnsWithSelection = useMemo(() => {
    if (!selectable) return columns;
    
    return [
      {
        Header: () => (
          <input
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAll}
            aria-label="Seleccionar todos"
          />
        ),
        accessor: 'selection',
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selected.includes(row.original[idField])}
            onChange={() => handleSelectionChange(row.original[idField])}
            aria-label={`Seleccionar fila ${row.original[idField]}`}
          />
        ),
        disableSortBy: true,
        width: 50
      },
      ...columns
    ];
  }, [columns, selectable, selectAll, selected, handleSelectionChange, handleSelectAll, idField]);

  const getSortIndicator = useCallback((column) => {
    if (column.disableSortBy || !column.accessor) return null;
    if (sortConfig.key !== column.accessor) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  }, [sortConfig]);

  const renderCell = useCallback((column, item) => {
    const value = item[column.accessor];
    
    if (column.Cell) {
      return <column.Cell row={{ original: item }} value={value} />;
    }
    
    if (column.format && value) {
      return column.format(value);
    }
    
    return value;
  }, []);

  if (loading) {
    return (
      <div className="data-table-container">
        <div className="data-table-loading">
          <div className="loading-spinner" />
          <span>Cargando...</span>
        </div>
      </div>
    );
  }

  if (!sortedData.length) {
    const showReloadButton = !emptyStateNoReload;
    
    return (
      <div className="data-table-container">
        <table className={`data-table ${className}`}>
          <thead>
            <tr>
              {columnsWithSelection.map((column, index) => (
                <th key={index} style={column.width ? { width: column.width } : {}}>
                  {column.Header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={columnsWithSelection.length} className="data-table-empty">
                <div className="empty-state-content">
                  <p>{emptyMessage}</p>
                  {showReloadButton && (
                    <button 
                      onClick={() => window.location.reload()}
                      className="reload-button"
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        background: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Recargar página
                    </button>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      <div className="data-table-search">
        <input
          type="text"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button 
            onClick={() => setSearchTerm('')}
            className="search-clear"
            title="Limpiar búsqueda"
          >
            ✕
          </button>
        )}
        {searchTerm && (
          <span className="search-results-count">
            {sortedData.length} resultado{sortedData.length !== 1 ? 's' : ''} de {data.length}
          </span>
        )}
      </div>
      <table className={`data-table ${className}`}>
        <thead>
          <tr>
            {columnsWithSelection.map((column, index) => (
              <th 
                key={index} 
                style={column.width ? { width: column.width } : {}}
                className={column.disableSortBy ? 'no-sort' : 'sortable'}
                onClick={() => handleSort(column)}
              >
                {typeof column.Header === 'function' ? <column.Header /> : column.Header}
                {getSortIndicator(column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((item) => (
            <tr 
              key={item[idField]} 
              className={selected.includes(item[idField]) ? 'row-selected' : ''}
            >
              {columnsWithSelection.map((column, index) => (
                <td key={index}>
                  {renderCell(column, item)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      
      {pageSize && sortedData.length > pageSize && (
        <div className="data-table-pagination">
          <button
            onClick={() => onPageChange && onPageChange(1)}
            disabled={currentPage === 1}
            className="pagination-button"
            title="Primera página"
          >
            ««
          </button>
          <button
            onClick={() => onPageChange && onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Anterior
          </button>
          <span className="pagination-info">
            Página {currentPage} de {Math.ceil(sortedData.length / pageSize)}
          </span>
          <button
            onClick={() => onPageChange && onPageChange(currentPage + 1)}
            disabled={currentPage >= Math.ceil(sortedData.length / pageSize)}
            className="pagination-button"
          >
            Siguiente
          </button>
          <button
            onClick={() => onPageChange && onPageChange(Math.ceil(sortedData.length / pageSize))}
            disabled={currentPage >= Math.ceil(sortedData.length / pageSize)}
            className="pagination-button"
            title="Última página"
          >
            »»
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(DataTable);
